import { useTracking } from '@/libs/analytics';
import { TrackStepNavFn } from '@/libs/analytics/types';
import { EnumFeature, EnumScreenName } from '@calm/evexia-analytics';
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useRef,
} from 'react';

interface PreDialogScreen {
    screenName: EnumScreenName;
    feature: EnumFeature;
}

interface DialogScreenOverrideContextValue {
    trackDialogPageNav: TrackStepNavFn;
    trackDialogClose: () => void;
}

const DialogScreenOverrideContext =
    createContext<DialogScreenOverrideContextValue | null>(null);

export function DialogScreenOverrideProvider({ children }: PropsWithChildren) {
    const { trackAsPageNav, restoreScreenName, state } = useTracking();
    const preDialogScreen = useRef<PreDialogScreen | null>(null);
    // Keep refs to latest values so both callbacks have stable empty deps
    const stateRef = useRef(state);
    stateRef.current = state;
    const trackAsPageNavRef = useRef(trackAsPageNav);
    trackAsPageNavRef.current = trackAsPageNav;
    const restoreScreenNameRef = useRef(restoreScreenName);
    restoreScreenNameRef.current = restoreScreenName;

    const trackDialogPageNav = useCallback<TrackStepNavFn>(
        (screenName, feature, query, additionalProps) => {
            if (!preDialogScreen.current) {
                preDialogScreen.current = {
                    screenName: stateRef.current.currentScreenName,
                    feature: stateRef.current.currentFeature,
                };
            }
            trackAsPageNavRef.current(
                screenName,
                feature,
                query,
                additionalProps
            );
        },
        []
    );

    const trackDialogClose = useCallback(() => {
        if (!preDialogScreen.current) {
            return;
        }
        restoreScreenNameRef.current(
            preDialogScreen.current.screenName,
            preDialogScreen.current.feature
        );
        preDialogScreen.current = null;
    }, []);

    const contextValue = useMemo(
        () => ({ trackDialogPageNav, trackDialogClose }),
        [trackDialogPageNav, trackDialogClose]
    );

    return (
        <DialogScreenOverrideContext.Provider value={contextValue}>
            {children}
        </DialogScreenOverrideContext.Provider>
    );
}

export function useDialogScreenOverride(): DialogScreenOverrideContextValue {
    const ctx = useContext(DialogScreenOverrideContext);
    if (process.env.NODE_ENV !== 'production' && !ctx) {
        throw new Error(
            'useDialogScreenOverride must be used within a DialogScreenOverrideProvider'
        );
    }
    // We want a silent error on prod since this is just analytics
    return ctx as DialogScreenOverrideContextValue;
}
